import { useQuery } from "@tanstack/react-query";
import type { Account } from "@shared/schema";
import { InlineSelectField, InlineDateField } from "@/components/inline-edit";

interface EntitySpecificFieldsProps {
  account: Account;
  onFieldUpdate: (field: keyof Account, value: any) => Promise<void>;
}

const TRUST_TYPE_OPTIONS = [
  { value: "family", label: "Family Trust" },
  { value: "unit", label: "Unit Trust" },
  { value: "discretionary", label: "Discretionary Trust" },
  { value: "testamentary", label: "Testamentary Trust" },
  { value: "hybrid", label: "Hybrid Trust" },
  { value: "bare_trust", label: "Bare Trust" },
  { value: "smsf", label: "SMSF" },
];

const TRUSTEE_TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "corporate", label: "Corporate" },
];

const COMPANY_CLASS_OPTIONS = [
  { value: "proprietary_limited", label: "Proprietary Limited" },
  { value: "public_limited", label: "Public Limited" },
  { value: "foreign_company", label: "Foreign Company" },
];

const PARTNERSHIP_TYPE_OPTIONS = [
  { value: "general", label: "General Partnership" },
  { value: "limited", label: "Limited Partnership" },
];

const SMSF_COMPLYING_STATUS_OPTIONS = [
  { value: "complying", label: "Complying" },
  { value: "non_complying", label: "Non-Complying" },
  { value: "pending", label: "Pending" },
];

export function EntitySpecificFields({ account, onFieldUpdate }: EntitySpecificFieldsProps) {
  // Check entity type flags for conditional rendering
  const isTrust = account.entityType === "trust";
  const isSmsfTrust = isTrust && account.trustType === "smsf";
  const isBareTrust = isTrust && account.trustType === "bare_trust";

  // Fetch SMSF accounts for the linked SMSF dropdown (only for bare trusts)
  // Hook must be called unconditionally at top level
  const { data: smsfAccounts } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { trustType: "smsf" }],
    enabled: isBareTrust,
  });
  const { data: linkedSmsf } = useQuery<Account>({
    queryKey: ["/api/crm/accounts", account.linkedSmsfId],
    enabled: isBareTrust && !!account.linkedSmsfId && !smsfAccounts?.some(item => item.id === account.linkedSmsfId),
  });

  // Build options for linked SMSF select
  const linkedSmsfOptions = (smsfAccounts ?? [])
    .filter((acc) => acc.trustType === "smsf" && acc.id !== account.id)
    .map((acc) => ({
      value: acc.id,
      label: acc.legalName + (acc.tradingName ? ` (T/A ${acc.tradingName})` : ""),
    }));

  // Format timestamp to date string for the date field
  const atoCheckedDateValue = account.smsfAtoCheckedAt
    ? new Date(account.smsfAtoCheckedAt).toISOString().split('T')[0]
    : null;

  if (account.entityType === "company") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="entity-fields-company">
        <InlineSelectField
          value={account.companyClass}
          options={COMPANY_CLASS_OPTIONS}
          onSave={(value) => onFieldUpdate("companyClass", value)}
          label="Company Class"
          placeholder="Select company class"
          testId="select-company-class"
        />
      </div>
    );
  }

  if (isTrust) {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="entity-fields-trust">
        <InlineSelectField
          value={account.trustType}
          options={TRUST_TYPE_OPTIONS}
          onSave={(value) => onFieldUpdate("trustType", value)}
          label="Trust Type"
          placeholder="Select trust type"
          testId="select-trust-type"
        />
        <InlineSelectField
          value={account.trusteeType}
          options={TRUSTEE_TYPE_OPTIONS}
          onSave={(value) => onFieldUpdate("trusteeType", value)}
          label="Trustee Type"
          placeholder="Select trustee type"
          testId="select-trustee-type"
        />
        <InlineDateField
          value={account.trustDeedDate}
          onSave={(value) => onFieldUpdate("trustDeedDate", value)}
          label="Trust Deed Date"
          placeholder="Select date"
          testId="input-trust-deed-date"
        />

        {/* SMSF-specific fields - only shown when trust_type = 'smsf' */}
        {isSmsfTrust && (
          <>
            <InlineSelectField
              value={account.smsfComplyingStatus}
              options={SMSF_COMPLYING_STATUS_OPTIONS}
              onSave={(value) => onFieldUpdate("smsfComplyingStatus", value)}
              label="SMSF Complying Status"
              placeholder="Select compliance status"
              testId="select-smsf-complying-status"
            />
            <InlineDateField
              value={atoCheckedDateValue}
              onSave={(value) => onFieldUpdate("smsfAtoCheckedAt", value ? new Date(value).toISOString() : null)}
              label="ATO Checked Date"
              placeholder="Select date"
              testId="input-smsf-ato-checked-at"
            />
          </>
        )}

        {/* Linked SMSF field - only shown when trust_type = 'bare_trust' */}
        {isBareTrust && (
          <InlineSelectField
            value={account.linkedSmsfId}
            selectedLabel={linkedSmsf && linkedSmsf.id === account.linkedSmsfId
              ? linkedSmsf.legalName + (linkedSmsf.tradingName ? ` (T/A ${linkedSmsf.tradingName})` : "") : undefined}
            options={linkedSmsfOptions.length > 0 ? linkedSmsfOptions : []}
            onSave={(value) => onFieldUpdate("linkedSmsfId", value)}
            label="Linked SMSF"
            placeholder={linkedSmsfOptions.length > 0 ? "Select linked SMSF" : "No SMSFs available"}
            disabled={linkedSmsfOptions.length === 0}
            testId="select-linked-smsf"
          />
        )}
      </div>
    );
  }

  if (account.entityType === "partnership") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="entity-fields-partnership">
        <InlineSelectField
          value={account.partnershipType}
          options={PARTNERSHIP_TYPE_OPTIONS}
          onSave={(value) => onFieldUpdate("partnershipType", value)}
          label="Partnership Type"
          placeholder="Select partnership type"
          testId="select-partnership-type"
        />
      </div>
    );
  }

  if (account.entityType === "sole_trader") {
    return (
      <div className="py-2" data-testid="entity-fields-sole-trader">
        <p className="text-sm text-muted-foreground">
          Sole trader fields managed via Contact record
        </p>
      </div>
    );
  }

  return null;
}
